import { useEffect } from "react";
import { Wrench, Phone, Mail, MapPin } from "lucide-react";

const BRAND = {
  longName: "Rechtsanwalt Matthias Oliver Keller",
  street: "Hauptstraße 79",
  zip: "53797",
  city: "Lohmar",
  phone: "02246 / 9159-708",
  phoneIntl: "+4922469159708",
  email: "info@recht-keller.de",
  emergency: "+49 151 22311309",
};

export const ComingSoon = () => {
  useEffect(() => {
    document.title = "In Bearbeitung · Rechtsanwalt Keller";
  }, []);

  return (
    <div className="min-h-screen bg-[#fafaf7] text-[#0a1128] relative overflow-hidden">
      {/* Subtle grain backdrop */}
      <div className="absolute inset-0 grain pointer-events-none opacity-50" />

      {/* Top brand bar */}
      <header className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 flex items-center justify-between">
        <div className="font-display text-[15px] font-bold tracking-tight flex items-center gap-2">
          <span className="inline-block w-2 h-2 bg-[#d90429] rounded-full" />
          <span>RA Keller</span>
          <span className="hidden sm:inline text-[#505a74] font-normal">
            · Lohmar
          </span>
        </div>
        <a
          href={`tel:${BRAND.phoneIntl}`}
          data-testid="coming-phone"
          className="hidden md:inline-flex items-center gap-2 text-[13px] font-medium text-[#0a1128]/80 hover:text-[#d90429] transition"
        >
          <Phone className="w-4 h-4" />
          {BRAND.phone}
        </a>
      </header>

      {/* Main */}
      <main className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 md:pt-28 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-[#505a74] mb-6">
              <span className="inline-flex w-9 h-9 rounded-full bg-[#0a1128] text-white items-center justify-center">
                <Wrench className="w-4 h-4 text-[#d90429]" />
              </span>
              <span className="eyebrow">Website im Aufbau</span>
            </div>

            <h1
              data-testid="coming-headline"
              className="font-display font-black tracking-tight text-[#0a1128] leading-[0.95] text-[44px] sm:text-[68px] lg:text-[96px]"
            >
              Wir bauen
              <br />
              gerade an
              <br />
              etwas{" "}
              <span className="italic font-light text-[#505a74]">Neuem</span>
              <span className="text-[#d90429]">.</span>
            </h1>

            <p className="mt-8 max-w-xl text-[17px] md:text-[19px] leading-relaxed text-[#505a74]">
              Unsere neue Online-Präsenz ist in Vorbereitung. In wenigen Tagen
              finden Sie hier den überarbeiteten Auftritt der Kanzlei{" "}
              <strong className="text-[#0a1128]">
                Rechtsanwalt M.&nbsp;O.&nbsp;Keller
              </strong>{" "}
              — klar, modern und ohne Umwege zur Beratung.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={`tel:${BRAND.phoneIntl}`}
                data-testid="coming-cta-call"
                className="btn-primary px-7 py-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Jetzt anrufen
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="coming-cta-email"
                className="btn-ghost px-7 py-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Per E-Mail schreiben
              </a>
            </div>

            <p className="mt-8 text-[12px] text-[#505a74]">
              Bis dahin sind wir telefonisch und per E-Mail wie gewohnt
              erreichbar.
            </p>
          </div>

          {/* Right: contact card */}
          <aside className="lg:col-span-5 lg:pt-16">
            <div className="bg-white border border-[#e8e6df] rounded-sm p-7 md:p-9 relative">
              <div className="absolute -top-3 left-7 bg-[#fafaf7] px-3">
                <span className="eyebrow text-[#d90429]">Direktkontakt</span>
              </div>

              <ul className="mt-2 space-y-5">
                <li className="flex items-start gap-4">
                  <Phone className="w-4 h-4 text-[#d90429] mt-1 shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#505a74] font-semibold">
                      Telefon
                    </div>
                    <a
                      href={`tel:${BRAND.phoneIntl}`}
                      className="block mt-1 font-display text-[20px] font-bold text-[#0a1128] hover:text-[#d90429] transition"
                    >
                      {BRAND.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4 border-t border-[#e8e6df] pt-5">
                  <Mail className="w-4 h-4 text-[#d90429] mt-1 shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#505a74] font-semibold">
                      E-Mail
                    </div>
                    <a
                      href={`mailto:${BRAND.email}`}
                      className="block mt-1 font-display text-[18px] font-bold text-[#0a1128] hover:text-[#d90429] transition break-all"
                    >
                      {BRAND.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4 border-t border-[#e8e6df] pt-5">
                  <MapPin className="w-4 h-4 text-[#d90429] mt-1 shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#505a74] font-semibold">
                      Anschrift
                    </div>
                    <div className="mt-1 text-[14px] text-[#0a1128] leading-relaxed">
                      {BRAND.longName}
                      <br />
                      {BRAND.street}
                      <br />
                      {BRAND.zip} {BRAND.city}
                    </div>
                  </div>
                </li>
              </ul>

              <div className="mt-7 pt-5 border-t border-[#e8e6df] text-[11px] text-[#505a74] leading-relaxed">
                <strong className="text-[#0a1128]">Sprechzeiten:</strong> Mo. –
                Fr. 09 – 13 & 14 – 18 Uhr. Termine außerhalb der Bürozeiten nach
                Vereinbarung.
                <div className="mt-2">
                  In dringenden Fällen:{" "}
                  <a
                    href={`tel:${BRAND.emergency.replace(/\s/g, "")}`}
                    className="text-[#0a1128] font-semibold"
                  >
                    {BRAND.emergency}
                  </a>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Footer marquee */}
      <footer className="relative bg-[#0a1128] text-white py-8 mt-8 overflow-hidden">
        <div className="flex marquee whitespace-nowrap font-display font-black text-[44px] md:text-[72px] tracking-tight leading-none opacity-90">
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="px-8 inline-flex items-center gap-8">
              In Bearbeitung.
              <span className="inline-block w-3 h-3 rounded-full bg-[#d90429]" />
            </span>
          ))}
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 mt-8 text-[11px] text-white/40 flex flex-wrap items-center justify-between gap-4">
          <span>© {new Date().getFullYear()} {BRAND.longName}</span>
          <span>Rhein-Sieg-Kreis · Bonn · Köln</span>
        </div>
      </footer>
    </div>
  );
};
